import React, { FC } from 'react';
import { TextV2 } from 'components/TextV2/TextV2';
import { useTranslation } from 'react-i18next';
import { PRICING } from 'components/Pricing/utils/pricingHookTranslationStrings';
import styles from './UpfrontAndMonthlyPricing.module.scss';
import { SoftwarePlanMonthlyPricing } from 'components/Pricing/PricingComponents/SoftwarePlanMonthlyPricing/SoftwarePlanMonthlyPricing';
import { MsrpPricing } from 'components/Pricing/PricingComponents/MsrpPricing/MsrpPricing';

interface IPriceDisplayProps {
  showMonthlyPriceFirst: boolean;
  msrpPrice: number;
  upfrontPrice: number;
  subscriptionMonthlyPrice: number;
  subscriptionsTranslationText: string;
  softwarePlanMonthlyPrice: number;
}

/**
 * Renders upfront and monthly pricing based on the provided prices and the "show monthly first" flag.
 *
 * Use case 1: If showMonthlyPriceFirst is true, the component displays
 *    the subscription price as the primary price and the upfront price as the secondary price
 *    plus softwarePlanMonthlyPrice if value is defined.
 * Use case 2: If showMonthlyPriceFirst is false, the component displays
 *    the upfront price as the primary price plus softwarePlanMonthlyPrice if value is defined
 *    and the subscription price as the secondary price.
 * Use case 3: If enable-msrp feature flag is true, shows msrp price above the primary price.
 *
 * @param {boolean} showMonthlyPriceFirst - Flag indicating whether to show the subscription price first.
 * @param {number} msrpPrice - The msrp price.
 * @param {number} upfrontPrice - The upfront price.
 * @param {number} subscriptionMonthlyPrice - The monthly subscription price.
 * @param {string} subscriptionsTranslationText - The translation text for the subscription price.
 * @param {number} softwarePlanMonthlyPrice - The monthly price for a software plan.
 *
 * @returns {JSX.Element} The rendered JSX for the UpfrontAndMonthlyPricing component.
 */
export const UpfrontAndMonthlyPricing: FC<IPriceDisplayProps> = ({
  showMonthlyPriceFirst,
  msrpPrice,
  upfrontPrice,
  subscriptionMonthlyPrice,
  subscriptionsTranslationText,
  softwarePlanMonthlyPrice,
}) => {
  const { t } = useTranslation();

  if (showMonthlyPriceFirst) {
    return (
      <>
        <MsrpPricing msrpPrice={msrpPrice} />
        <TextV2
          variant="body-medium"
          value={t(PRICING.STRING_MONTHLY_PRICE, { price: subscriptionMonthlyPrice })}
        />
        <TextV2 className={styles.copyPadding} variant="body-small" value={t(subscriptionsTranslationText)} />
        <span className={styles.upfrontAsSecondaryPrice} />
        <TextV2
          variant="body-small"
          value={t(PRICING.STRING_UPFRONT_AS_SECONDARY_PRICE, { price: upfrontPrice })}
        />
        <SoftwarePlanMonthlyPricing softwarePlanMonthlyPrice={softwarePlanMonthlyPrice} />
      </>
    );
  }

  return (
    <>
      <MsrpPricing msrpPrice={msrpPrice} />
      <TextV2 variant="body-medium" value={t(PRICING.STRING_UPFRONT, { price: upfrontPrice })} />
      <SoftwarePlanMonthlyPricing softwarePlanMonthlyPrice={softwarePlanMonthlyPrice} />
      <TextV2
        className={styles.subscriptionMonthlyPrice}
        variant="body-small"
        value={t(subscriptionsTranslationText, { price: subscriptionMonthlyPrice })}
      />
    </>
  );
};
